import React from 'react';
import { Badge } from 'components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { usePublishingIngestRuns } from 'hooks/swrRequests/usePublishingAdmin';
import { formatShortDate } from 'utils/helpers';

// Collab's date plus a 24h time; runs can be minutes apart.
const fmt = (iso: string | null) =>
  iso
    ? `${formatShortDate(iso)} ${new Date(iso).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : '—';

const th = 'text-sm font-bold uppercase tracking-[0.04em] text-[#64748b]'; // Collab PlainHeaderCell

const statusClass: Record<string, string> = {
  done: 'border-transparent bg-green-100 text-green-800 hover:bg-green-100',
  failed: 'border-transparent bg-red-100 text-red-800 hover:bg-red-100',
  running: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
};

export function IngestRuns() {
  const { runs, isLoading, error } = usePublishingIngestRuns(20);
  return (
    <div>
      {error && <p className="m-0 px-5 py-3 text-base text-red-600">{error.message}</p>}
      {isLoading && <p className="m-0 px-5 py-3 text-base text-gray-500">Loading…</p>}
      {!isLoading && !runs.length && (
        <p className="m-0 px-5 py-3 text-base text-gray-500">No runs yet.</p>
      )}
      {!!runs.length && (
        <div className="overflow-x-auto">
          <Table className="text-base">
            <TableHeader>
              <TableRow>
                <TableHead className={th}>Run</TableHead>
                <TableHead className={th}>Status</TableHead>
                <TableHead className={th}>Started</TableHead>
                <TableHead className={th}>Finished</TableHead>
                <TableHead className={th}>Source run</TableHead>
                <TableHead className={th}>Counts</TableHead>
                <TableHead className={th}>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="tabular-nums">{r.id}</TableCell>
                  <TableCell>
                    <Badge className={`text-sm ${statusClass[r.status]}`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{fmt(r.startedAt)}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmt(r.finishedAt)}</TableCell>
                  <TableCell className="tabular-nums">{r.sourceRunId || '—'}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {r.counts
                      ? Object.entries(r.counts).map(([k, v]) => (
                          <div key={k} className="whitespace-nowrap">
                            {k} {v}
                          </div>
                        ))
                      : '—'}
                  </TableCell>
                  <TableCell className="max-w-[320px] break-words text-sm text-red-700">
                    {r.error || ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
